import React from "react";
import { AlertTriangle } from "lucide-react";
import { Sentry } from "../sentry";

interface State {
  hasError: boolean;
  error: Error | null;
  eventId: string | null;
}

function ErrorFallback({ error, eventId, onRetry }: { error: Error | null; eventId: string | null; onRetry: () => void }) {
  const supportEmail = "support@fencemetric.com";
  const subject = encodeURIComponent(`FenceMetric error report${eventId ? ` (ref: ${eventId})` : ""}`);
  const body = encodeURIComponent(
    `Hi FenceMetric team,\n\nI ran into an error while using the app.\n\n` +
      (eventId ? `Reference ID: ${eventId}\n` : "") +
      (error?.message ? `Error message: ${error.message}\n` : "") +
      `\nWhat I was doing:\n`,
  );

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center p-8 text-center">
      <div className="max-w-md w-full">
        <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="h-8 w-8 text-rose-600" />
        </div>
        <h1 className="text-xl font-semibold text-slate-900 mb-2">Something went wrong</h1>
        <p className="text-slate-500 mb-2">
          An unexpected error occurred on this page. The issue has been reported.
        </p>
        {eventId && (
          <p className="text-xs text-slate-400 mb-4 font-mono">Reference: {eventId}</p>
        )}
        {import.meta.env.DEV && error && (
          <pre className="mb-4 p-3 bg-slate-100 rounded text-left text-xs text-slate-700 overflow-auto max-h-40 whitespace-pre-wrap">
            {error.message}
          </pre>
        )}
        <button
          onClick={onRetry}
          className="bg-primary text-primary-foreground px-6 py-2 rounded-lg font-medium hover:bg-primary/90 transition-colors"
        >
          Try again
        </button>
        <button
          onClick={() => window.location.reload()}
          className="block mx-auto mt-3 text-sm text-slate-400 hover:text-slate-600 transition-colors"
        >
          Refresh page
        </button>
        <a
          href={`mailto:${supportEmail}?subject=${subject}&body=${body}`}
          className="block mx-auto mt-2 text-sm text-primary hover:underline"
        >
          Report this issue
        </a>
      </div>
    </div>
  );
}

export class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  State
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null, eventId: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    const eventId = Sentry.captureException(error, {
      contexts: {
        react: { componentStack: info.componentStack ?? undefined },
      },
    });
    this.setState({ eventId });
    if (import.meta.env.DEV) {
      console.error("[ErrorBoundary]", error, info);
    }
  }

  resetError = () => {
    this.setState({ hasError: false, error: null, eventId: null });
  };

  render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} eventId={this.state.eventId} onRetry={this.resetError} />;
    }
    return this.props.children;
  }
}
